/**
 * RecipeEditForm — Shared recipe create/edit form.
 * Used by:
 *   - Manual entry (empty initialData)
 *   - Photo scan review (pre-populated from Claude)
 *   - URL import review (pre-populated from Claude)
 *   - Editing an existing recipe (pre-populated from DB)
 *
 * Ported from the personal prototype's RecipeEditForm.js,
 * adapted for TableTryb's API (household-scoped, useApi hook).
 */
import React, { useState, useEffect, useRef } from 'react';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../context/AuthContext';

interface Ingredient {
  name: string;
  amount?: number;
  unit?: string;
  original?: string;
  aisle?: string;
}

interface Instruction {
  number?: number;
  step: string;
}

interface RecipeData {
  recipeId?: string;
  title?: string;
  image?: string;
  candidateImages?: string[];
  readyInMinutes?: number;
  servings?: number;
  sourceUrl?: string;
  summary?: string;
  cuisines?: string[] | string;
  dishTypes?: string[] | string;
  diets?: string[];
  ingredients?: Ingredient[];
  instructions?: Instruction[];
  source?: string;
}

interface Props {
  initialData?: RecipeData | null;
  onSave: () => void;
  onCancel?: () => void;
  showToast: (msg: string) => void;
  isEdit?: boolean;
}

const RecipeEditForm: React.FC<Props> = ({
  initialData,
  onSave,
  onCancel,
  showToast,
  isEdit = false,
}) => {
  const apiRef = useRef(useApi());
  const { user } = useAuth();

  const [form, setForm] = useState({
    title: '',
    readyInMinutes: '',
    servings: '4',
    sourceUrl: '',
    cuisines: '',
    dishTypes: 'main course',
    summary: '',
  });

  const [ingredients, setIngredients] = useState<
    { original: string; name: string; amount: number; unit: string; aisle: string }[]
  >([{ original: '', name: '', amount: 0, unit: '', aisle: '' }]);

  const [instructions, setInstructions] = useState<{ step: string }[]>([
    { step: '' },
  ]);

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [existingImageUrl, setExistingImageUrl] = useState('');
  const [imageUrlInput, setImageUrlInput] = useState('');
  const [imageMode, setImageMode] = useState<'keep' | 'upload' | 'url'>('upload');
  const [candidateImages, setCandidateImages] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  // Populate from initialData
  useEffect(() => {
    if (!initialData) return;
    setForm({
      title: initialData.title || '',
      readyInMinutes: initialData.readyInMinutes
        ? String(initialData.readyInMinutes)
        : '',
      servings: initialData.servings ? String(initialData.servings) : '4',
      sourceUrl: initialData.sourceUrl || '',
      cuisines: Array.isArray(initialData.cuisines)
        ? initialData.cuisines.join(', ')
        : (initialData.cuisines as string) || '',
      dishTypes: Array.isArray(initialData.dishTypes)
        ? initialData.dishTypes[0] || 'main course'
        : (initialData.dishTypes as string) || 'main course',
      summary: initialData.summary || '',
    });

    if (initialData.ingredients?.length) {
      setIngredients(
        initialData.ingredients.map((ing) => ({
          original: ing.original || ing.name || '',
          name: ing.name || '',
          amount: ing.amount || 0,
          unit: ing.unit || '',
          aisle: ing.aisle || '',
        }))
      );
    }

    if (initialData.instructions?.length) {
      setInstructions(
        initialData.instructions.map((inst) => ({
          step: inst.step || '',
        }))
      );
    }

    if (initialData.image) {
      setExistingImageUrl(initialData.image);
      setImageMode('keep');
    } else {
      setImageMode('upload');
    }

    // Handle candidate images from URL import
    if (initialData.candidateImages?.length) {
      setCandidateImages(initialData.candidateImages);
      // Auto-select first candidate if no image was set
      if (!initialData.image && initialData.candidateImages.length > 0) {
        setExistingImageUrl(initialData.candidateImages[0]);
        setImageMode('keep');
      }
    } else {
      setCandidateImages([]);
    }
  }, [initialData]);

  const updateField = (field: string, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  // ---- Image handling ----
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showToast('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showToast('Image must be under 5MB');
      return;
    }
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  // ---- Ingredient helpers ----
  const addIngredient = () =>
    setIngredients((prev) => [
      ...prev,
      { original: '', name: '', amount: 0, unit: '', aisle: '' },
    ]);

  const removeIngredient = (idx: number) =>
    setIngredients((prev) => prev.filter((_, i) => i !== idx));

  const updateIngredient = (idx: number, field: string, value: string | number) =>
    setIngredients((prev) =>
      prev.map((ing, i) => (i === idx ? { ...ing, [field]: value } : ing))
    );

  // ---- Instruction helpers ----
  const addInstruction = () =>
    setInstructions((prev) => [...prev, { step: '' }]);

  const removeInstruction = (idx: number) =>
    setInstructions((prev) => prev.filter((_, i) => i !== idx));

  const updateInstruction = (idx: number, value: string) =>
    setInstructions((prev) =>
      prev.map((inst, i) => (i === idx ? { step: value } : inst))
    );

  // ---- Submit ----
  const handleSubmit = async () => {
    if (!form.title.trim()) {
      showToast('Recipe title is required');
      return;
    }
    if (!user?.householdId) {
      showToast('No household found');
      return;
    }

    setSaving(true);
    try {
      // Resolve image URL
      let finalImageUrl = existingImageUrl;

      if (imageMode === 'upload' && imageFile) {
        // Upload to S3 via presigned URL
        try {
          const { uploadUrl, imageUrl } = await apiRef.current.post<{
            uploadUrl: string;
            imageUrl: string;
          }>(`/v1/households/${user.householdId}/images/presigned-url`, {
            contentType: imageFile.type,
            fileName: imageFile.name,
          });

          // PUT the file directly to S3
          await fetch(uploadUrl, {
            method: 'PUT',
            headers: { 'Content-Type': imageFile.type },
            body: imageFile,
          });

          finalImageUrl = imageUrl;
        } catch (uploadErr) {
          console.error('Image upload failed:', uploadErr);
          showToast('Image upload failed — saving recipe without photo');
          finalImageUrl = existingImageUrl || '';
        }
      } else if (imageMode === 'url' && imageUrlInput.trim()) {
        finalImageUrl = imageUrlInput.trim();
      }

      const cleanIngredients = ingredients
        .filter((ing) => ing.original.trim())
        .map((ing) => ({
          name: ing.name || ing.original.trim(),
          amount: ing.amount || 0,
          unit: ing.unit || '',
          original: ing.original.trim(),
          aisle: ing.aisle || '',
        }));

      const cleanInstructions = instructions
        .filter((inst) => inst.step.trim())
        .map((inst, idx) => ({
          number: idx + 1,
          step: inst.step.trim(),
        }));

      // Determine source type
      let source = 'manual';
      if (isEdit && initialData?.source) {
        source = initialData.source; // preserve original source on edit
      } else if (initialData?.sourceUrl) {
        source = 'claude-url';
      }

      const recipe = {
        title: form.title.trim(),
        image: finalImageUrl,
        readyInMinutes: parseInt(form.readyInMinutes) || 0,
        servings: parseInt(form.servings) || 4,
        sourceUrl: form.sourceUrl.trim(),
        summary: form.summary.trim(),
        cuisines: form.cuisines
          ? form.cuisines
              .split(',')
              .map((c) => c.trim())
              .filter(Boolean)
          : [],
        dishTypes: form.dishTypes ? [form.dishTypes.trim()] : ['main course'],
        diets: [],
        ingredients: cleanIngredients,
        instructions: cleanInstructions,
        source,
      };

      const basePath = `/v1/households/${user.householdId}/recipes`;

      if (isEdit && initialData?.recipeId) {
        await apiRef.current.put(`${basePath}/${initialData.recipeId}`, recipe);
      } else {
        await apiRef.current.post(basePath, recipe);
      }

      showToast(`"${recipe.title}" ${isEdit ? 'updated' : 'saved'}!`);
      onSave();
    } catch (err) {
      console.error(err);
      showToast('Failed to save recipe.');
    } finally {
      setSaving(false);
    }
  };

  const currentImage =
    imageMode === 'upload' && imagePreview
      ? imagePreview
      : imageMode === 'url' && imageUrlInput
        ? imageUrlInput
        : existingImageUrl;

  return (
    <div className="settings-panel" style={{ maxWidth: '700px' }}>
      <div className="settings-group">
        <h3>{isEdit ? '✏️ Edit Recipe' : '✏️ Add Recipe'}</h3>

        {/* Title */}
        <div className="manual-field">
          <label className="manual-label">
            Recipe Title <span style={{ color: 'var(--color-error)' }}>*</span>
          </label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => updateField('title', e.target.value)}
            placeholder="e.g., Grandma's Chicken Pot Pie"
          />
        </div>

        {/* Display Photo */}
        <div className="manual-field">
          <label className="manual-label">Display Photo</label>
          {currentImage && (
            <img
              src={currentImage}
              alt="Recipe"
              className="image-preview"
              style={{ marginBottom: '12px' }}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          )}
          <div
            style={{
              display: 'flex',
              gap: '8px',
              marginBottom: '12px',
              flexWrap: 'wrap',
            }}
          >
            {existingImageUrl && (
              <button
                className={`btn ${imageMode === 'keep' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setImageMode('keep')}
                style={{ padding: '6px 14px', fontSize: '0.82rem' }}
              >
                ✓ Keep Current
              </button>
            )}
            <button
              className={`btn ${imageMode === 'upload' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setImageMode('upload')}
              style={{ padding: '6px 14px', fontSize: '0.82rem' }}
            >
              📁 Upload New
            </button>
            <button
              className={`btn ${imageMode === 'url' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setImageMode('url')}
              style={{ padding: '6px 14px', fontSize: '0.82rem' }}
            >
              🔗 Paste URL
            </button>
          </div>
          {/* Candidate image picker — shown when URL import found multiple photos */}
          {candidateImages.length > 1 && (
            <div style={{ marginBottom: '12px' }}>
              <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', marginBottom: '8px' }}>
                📷 {candidateImages.length} photos found on the recipe page — click to select:
              </p>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {candidateImages.map((imgUrl, idx) => (
                  <img
                    key={idx}
                    src={imgUrl}
                    alt={`Option ${idx + 1}`}
                    style={{
                      width: '80px',
                      height: '80px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      border:
                        existingImageUrl === imgUrl
                          ? '3px solid var(--color-primary)'
                          : '2px solid var(--color-border, #ddd)',
                    }}
                    onClick={() => {
                      setExistingImageUrl(imgUrl);
                      setImageUrlInput('');
                      setImageFile(null);
                      setImagePreview(null);
                      setImageMode('keep');
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ))}
              </div>
            </div>
          )}
          {imageMode === 'upload' && (
            <div>
              <div
                className="image-dropzone"
                onClick={() =>
                  document.getElementById('edit-image-input')?.click()
                }
              >
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="image-preview"
                  />
                ) : (
                  <div className="dropzone-placeholder">
                    <span style={{ fontSize: '2rem' }}>📷</span>
                    <span>Click to select a photo of the finished dish</span>
                  </div>
                )}
              </div>
              <input
                id="edit-image-input"
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </div>
          )}
          {imageMode === 'url' && (
            <input
              type="text"
              value={imageUrlInput}
              onChange={(e) => setImageUrlInput(e.target.value)}
              placeholder="https://example.com/photo.jpg"
            />
          )}
        </div>

        {/* Time and Servings */}
        <div style={{ display: 'flex', gap: '16px' }}>
          <div className="manual-field" style={{ flex: 1 }}>
            <label className="manual-label">Prep Time (min)</label>
            <input
              type="number"
              value={form.readyInMinutes}
              onChange={(e) => updateField('readyInMinutes', e.target.value)}
              placeholder="45"
            />
          </div>
          <div className="manual-field" style={{ flex: 1 }}>
            <label className="manual-label">Servings</label>
            <input
              type="number"
              value={form.servings}
              onChange={(e) => updateField('servings', e.target.value)}
              placeholder="4"
            />
          </div>
        </div>

        {/* Cuisine and Meal Type */}
        <div style={{ display: 'flex', gap: '16px' }}>
          <div className="manual-field" style={{ flex: 1 }}>
            <label className="manual-label">Cuisine</label>
            <input
              type="text"
              value={form.cuisines}
              onChange={(e) => updateField('cuisines', e.target.value)}
              placeholder="e.g., Italian, Mexican"
            />
          </div>
          <div className="manual-field" style={{ flex: 1 }}>
            <label className="manual-label">Meal Type</label>
            <select
              value={form.dishTypes}
              onChange={(e) => updateField('dishTypes', e.target.value)}
            >
              <option value="main course">Main Course</option>
              <option value="side dish">Side Dish</option>
              <option value="appetizer">Appetizer</option>
              <option value="dessert">Dessert</option>
              <option value="breakfast">Breakfast</option>
              <option value="soup">Soup</option>
              <option value="salad">Salad</option>
              <option value="snack">Snack</option>
            </select>
          </div>
        </div>

        {/* Source URL */}
        <div className="manual-field">
          <label className="manual-label">Source URL (optional)</label>
          <input
            type="url"
            value={form.sourceUrl}
            onChange={(e) => updateField('sourceUrl', e.target.value)}
            placeholder="https://www.allrecipes.com/recipe/..."
          />
        </div>

        {/* Summary */}
        <div className="manual-field">
          <label className="manual-label">Summary (optional)</label>
          <textarea
            value={form.summary}
            onChange={(e) => updateField('summary', e.target.value)}
            placeholder="A brief description of the dish..."
            rows={3}
          />
        </div>

        {/* Ingredients */}
        <div className="manual-field">
          <label className="manual-label">
            Ingredients
            <button
              className="btn btn-secondary"
              onClick={addIngredient}
              style={{
                marginLeft: '12px',
                padding: '2px 10px',
                fontSize: '0.75rem',
              }}
            >
              + Add
            </button>
          </label>
          {ingredients.map((ing, idx) => (
            <div
              key={idx}
              style={{
                display: 'flex',
                gap: '8px',
                marginBottom: '8px',
                alignItems: 'center',
              }}
            >
              <input
                type="text"
                value={ing.original}
                onChange={(e) =>
                  updateIngredient(idx, 'original', e.target.value)
                }
                placeholder={`Ingredient ${idx + 1} (e.g., 2 cups flour)`}
                style={{ flex: 1 }}
              />
              {ingredients.length > 1 && (
                <button
                  onClick={() => removeIngredient(idx)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--color-error)',
                    fontSize: '1.1rem',
                    padding: '4px',
                  }}
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Instructions */}
        <div className="manual-field">
          <label className="manual-label">
            Instructions
            <button
              className="btn btn-secondary"
              onClick={addInstruction}
              style={{
                marginLeft: '12px',
                padding: '2px 10px',
                fontSize: '0.75rem',
              }}
            >
              + Add Step
            </button>
          </label>
          {instructions.map((inst, idx) => (
            <div
              key={idx}
              style={{
                display: 'flex',
                gap: '8px',
                marginBottom: '8px',
                alignItems: 'flex-start',
              }}
            >
              <span
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  background: 'var(--color-primary)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  flexShrink: 0,
                  marginTop: '10px',
                }}
              >
                {idx + 1}
              </span>
              <textarea
                value={inst.step}
                onChange={(e) => updateInstruction(idx, e.target.value)}
                placeholder={`Step ${idx + 1}`}
                rows={2}
                style={{ flex: 1 }}
              />
              {instructions.length > 1 && (
                <button
                  onClick={() => removeInstruction(idx)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--color-error)',
                    fontSize: '1.1rem',
                    padding: '4px',
                    marginTop: '8px',
                  }}
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Save / Cancel */}
        <div style={{ display: 'flex', gap: '12px', marginTop: 'var(--space-lg)' }}>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={saving}
            style={{ flex: 1, justifyContent: 'center', padding: '14px' }}
          >
            {saving
              ? 'Saving...'
              : isEdit
                ? '💾 Save Changes'
                : '💾 Save Recipe'}
          </button>
          {onCancel && (
            <button
              className="btn btn-secondary"
              onClick={onCancel}
              style={{ padding: '14px 24px' }}
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecipeEditForm;